// Chat.tsx
import { useRef, useState, useEffect } from "react";
import readNDJSONStream from "ndjson-readablestream";
import { useSearchParams } from "react-router-dom";
import styles from "./Chat.module.css";

import {
  chatApi,
  RetrievalMode,
  ChatAppResponse,
  ChatAppResponseOrError,
  ChatAppRequest,
  ResponseMessage,
  VectorFieldOptions,
  GPT4VInput
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { ClearChatButton } from "../../components/ClearChatButton";
import { trackSearch } from "../../analytics";
import { useTranslation } from "../../i18n/useTranslation";
import searchIcon from "../../assets/search.svg";
import { useLocation } from "react-router-dom";

const disclaimerUrls: Record<string, string> = {
  de: "https://lifenet.swisslife.com/aisearch-disclaimer-de",
  fr: "https://lifenet.swisslife.com/aisearch-disclaimer-fr",
  it: "https://lifenet.swisslife.com/aisearch-disclaimer-it",
  en: "https://lifenet.swisslife.com/aisearch-disclaimer-en"
};

const helpUrls: Record<string, string> = {
  de: "https://lifenet.swisslife.com/aisearch-info-de",
  fr: "https://lifenet.swisslife.com/aisearch-info-fr",
  it: "https://lifenet.swisslife.com/aisearch-info-it",
  en: "https://lifenet.swisslife.com/aisearch-info-en"
};

type DP = {
  source_id?: string;
  metadata?: { title?: string; file_name?: string };
};

function useIsMobile(breakpointPx = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpointPx);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpointPx);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpointPx]);

  return isMobile;
}

function parseDataPoints(resp: ChatAppResponse): DP[] {
  const rawDp = (resp.choices?.[0]?.context as any)?.data_points;
  const rawArr: string[] = Array.isArray(rawDp) ? rawDp : Array.isArray(rawDp?.text) ? rawDp.text : [];
  return rawArr
    .map(txt => {
      try {
        return JSON.parse(txt) as DP;
      } catch {
        return null;
      }
    })
    .filter((x): x is DP => !!x);
}

function buildCitationMapFromResponse(resp: ChatAppResponse): Map<string, string> {
  const dps = parseDataPoints(resp);
  const map = new Map<string, string>();
  const counts = new Map<string, number>();
  for (const d of dps) {
    const sid = d.source_id;
    if (!sid) continue;
    const base = (d.metadata?.title?.trim()) || (d.metadata?.file_name?.trim()) || sid;
    const n = (counts.get(base) ?? 0) + 1;
    counts.set(base, n);
    const label = n === 1 ? base : `${base} (${n})`;
    if (!map.has(sid)) map.set(sid, label);
  }
  return map;
}

function replaceSourceIdsWithFilenames(text: string, citationMap: Map<string, string>): string {
  return text.replace(/\[(source_\d+)\]/g, (m, sid) => {
    const label = citationMap.get(sid);
    return label ? `[${label}]` : m;
  });
}

/** ---- Pragmatic one-retry helpers (transient only) ---- */
const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

const shouldRetryOnce = (response?: Response, err?: unknown) => {
  if (response) {
    const s = response.status;
    if (s >= 500) return true;        // transient server-side
    if (s === 408 || s === 429) return true; // timeout / throttling
    return false;                      // don't retry 4xx like 401/403/400
  }

  // If we have no response, it's likely network/stream abort -> retry once
  const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (msg.includes("aborted") || msg.includes("cancel") || msg.includes("timeout")) return true;
  if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to fetch")) return true;

  return false;
};

async function readBackendError(response: Response): Promise<string> {
  const ct = response.headers.get("content-type") || "";
  try {
    if (ct.includes("application/json")) {
      const j: any = await response.json();
      const msg = j?.error || j?.message || j?.detail || j?.title;
      return msg ? String(msg) : JSON.stringify(j);
    }
    const t = await response.text();
    return t || "";
  } catch {
    return "";
  }
}

const Chat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [promptTemplate, setPromptTemplate] = useState<string>("");
  const [retrieveCount, setRetrieveCount] = useState<number>(3);
  const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
  const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
  const [shouldStream, setShouldStream] = useState<boolean>(true);
  const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
  const [excludeCategory, setExcludeCategory] = useState<string>("");
  const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
  const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Embedding]);
  const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
  const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
  const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
  const [useGPT4V, setUseGPT4V] = useState<boolean>(false);
  const lastQuestionRef = useRef<string>("");
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<unknown>();
  const [activeCitation, setActiveCitation] = useState<string>();
  const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);
  const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
  const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
  const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
  const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const { t, isLoading: translationsLoading, lang, setLang } = useTranslation();
  const isMobile = useIsMobile(768);
  const tWithFallback = (primary: string, fallback: string) => {
    const value = t(primary);
    return !value || value === primary ? t(fallback) : value;
  };

  const newParams = new URLSearchParams(searchParams);
  newParams.delete("prompt");

  const examples: string[] = [];
  for (let i = 1; i <= 10; i++) {
    const key = `SearchSuggestion${i}Label`;
    const suggestion = t(key);
    if (!suggestion || suggestion === key) break;
    examples.push(suggestion);
  }

  const [suggestionText, setSuggestionText] = useState<string>("");
  const [inputKey, setInputKey] = useState<number>(0);

  /**
   * Streaming handler that is robust to event ordering:
   * - Sometimes delta.content arrives before the "base" event with data_points
   * - Avoids `Cannot read properties of undefined (reading '0')`
   */
  const handleAsyncRequest = async (
    question: string,
    _answers: [string, ChatAppResponse][],
    _setAnswers: Function,
    response: Response
  ) => {
    const responseBody = response.body;
    if (!responseBody) throw new Error(`No response body (HTTP ${response.status})`);

    let answer = "";
    let askResponse: ChatAppResponse | undefined;

    const pushStreamed = () => {
      if (!askResponse?.choices?.[0]?.message?.role) return;

      const latestResponse: ChatAppResponse = {
        ...askResponse,
        choices: [
          {
            ...askResponse.choices[0],
            message: { content: answer, role: askResponse.choices[0].message.role }
          }
        ]
      };
      setStreamedAnswers([[question, latestResponse]]);
    };

    try {
      setIsStreaming(true);

      for await (const event of readNDJSONStream(responseBody)) {
        if (event?.error) throw new Error(String(event.error));

        const choice0 = event?.choices?.[0];
        const delta = choice0?.delta;
        const ctx = choice0?.context;

        // Base response shape (data_points event)
        if (ctx?.data_points) {
          choice0.message = choice0.delta;
          askResponse = event as ChatAppResponse;
          continue;
        }

        // Delta content (may arrive before base) -> create minimal base
        if (delta?.content) {
          setIsLoading(false);

          if (!askResponse?.choices?.[0]) {
            askResponse = {
              choices: [
                {
                  message: { role: "assistant", content: "" } as any,
                  context: ctx ?? {}
                } as any
              ]
            } as ChatAppResponse;
          }

          answer += String(delta.content);
          await sleep(33); // keep your current throttling feel
          pushStreamed();
          continue;
        }

        // Context-only updates
        if (ctx) {
          if (!askResponse?.choices?.[0]) {
            askResponse = {
              choices: [
                {
                  message: { role: "assistant", content: "" } as any,
                  context: {}
                } as any
              ]
            } as ChatAppResponse;
          }
          askResponse.choices[0].context = { ...askResponse.choices[0].context, ...ctx };
        }
      }
    } finally {
      setIsStreaming(false);
    }

    if (!askResponse?.choices?.[0]?.message?.role) {
      throw new Error(`Invalid streaming response from backend (HTTP ${response.status}).`);
    }

    const fullResponse: ChatAppResponse = {
      ...askResponse,
            choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
    };
    return fullResponse;
  };

  const makeApiRequest = async (question: string) => {
    lastQuestionRef.current = question;

    if (error) setError(undefined);
    setIsLoading(true);
    setActiveCitation(undefined);
    setActiveAnalysisPanelTab(undefined);

    try {
      const messages: ResponseMessage[] = answers.flatMap(([userQ, resp]) => {
        const citMap = buildCitationMapFromResponse(resp);
        const rewritten = replaceSourceIdsWithFilenames(resp.choices[0].message.content, citMap);
        return [
          { content: userQ, role: "user" as const },
          { content: rewritten, role: "assistant" as const }
        ];
      });

      const request: ChatAppRequest = {
        messages: [...messages, { content: question, role: "user" }],
        stream: shouldStream,
        context: {
          overrides: {
            prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
            exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
            top: retrieveCount,
            retrieval_mode: retrievalMode,
            semantic_ranker: useSemanticRanker,
            semantic_captions: useSemanticCaptions,
            suggest_followup_questions: true,
            use_oid_security_filter: useOidSecurityFilter,
            use_groups_security_filter: useGroupsSecurityFilter,
            vector_fields: vectorFieldList,
            use_gpt4v: useGPT4V,
            gpt4v_input: gpt4vInput
          }
        },
        session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
      };

      let response: Response | undefined;
      let lastErr: unknown;

      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          response = await chatApi(request, undefined, lang);

          // Fail fast on non-OK and show backend message (no NDJSON parsing attempt)
          if (!response.ok) {
            const details = await readBackendError(response);
            const msg = details?.trim()
              ? `Backend error ${response.status}: ${details.trim()}`
              : `Backend error ${response.status}`;
            throw new Error(msg);
          }

          if (shouldStream) {
            setStreamedAnswers([]);
            const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answers, setAnswers, response);
            setAnswers([...answers, [question, parsedResponse]]);
            return;
          } else {
            const parsedResponse: ChatAppResponseOrError = await response.json();
            if (response.status > 299 || !response.ok) {
              throw new Error((parsedResponse as any)?.error || `HTTP ${response.status}`);
            }
            setAnswers([...answers, [question, parsedResponse as ChatAppResponse]]);
            return;
          }
        } catch (e) {
          lastErr = e;
          const retry = attempt === 0 && shouldRetryOnce(response, e);
          if (!retry) break;
          await sleep(400);
        }
      }

      throw lastErr;
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    lastQuestionRef.current = "";
    if (error) setError(undefined);
    setActiveCitation(undefined);
    setActiveAnalysisPanelTab(undefined);
    setAnswers([]);
    setStreamedAnswers([]);
    setIsLoading(false);
    setIsStreaming(false);
    setSuggestionText("");
    setInputKey(inputKey + 1);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("lang");
    newParams.delete("prompt");
    setSearchParams(newParams, { replace: true });
  };

  // NOTE: use block bodies here. A concise arrow body implicitly returns the
  // result of scrollIntoView(), and React treats an effect's return value as its
  // cleanup function. Natively scrollIntoView() returns undefined, but if a
  // smooth-scroll polyfill (present on some intranet/Adobe environments) overrides
  // Element.prototype.scrollIntoView to return a Promise, React later invokes that
  // Promise as destroy() -> "TypeError: destroy is not a function", crashing the app.
  useEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [isLoading]);
  useEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
  }, [streamedAnswers]);

  useEffect(() => {
    const initialPrompt = searchParams.get("prompt");
    const langFromQuery = searchParams.get("lang");
    if (langFromQuery && langFromQuery !== lang) setLang(langFromQuery);

    if (initialPrompt && !isLoading && answers.length === 0) {
      trackSearch(initialPrompt, {
        pageName: `${lang}:lifenet:ai-search`,
        title: document.title,
        url: window.location.origin + window.location.pathname,
        lang,
        domain: window.location.host,
        fullUrl: `${window.location.href}`
      });
      makeApiRequest(initialPrompt);
    }
  }, [searchParams]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onUseOidSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseOidSecurityFilter(!!checked);
    };

    const onUseGroupsSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseGroupsSecurityFilter(!!checked);
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

  const onShowCitation = (citation: string, index: number) => {
    console.log(citation);
        if (!citation.includes('.pdf') && !citation.includes('.jpg') && !citation.includes('.jpeg') && !citation.includes('.png')) {
            if (citation.includes("http")) {
                citation = citation.substring("/content/".length);
            }
            window.open(citation, '_blank');
    } else {
      if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
        setActiveAnalysisPanelTab(undefined);
      } else {
        setActiveCitation(citation);
        setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
      }
      setSelectedAnswer(index);
    }
  };

  const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }
    setSelectedAnswer(index);
  };

  return (
    <div className={styles.container}>
      <div className={styles.chatRoot}>
        <div className={styles.chatContainer}>
          {/* EMPTY STATE */}
          {!lastQuestionRef.current ? (
            <div className={styles.chatEmptyState}>
              <br />
              <br />
              <br />
              <h1 className={styles.chatEmptyStateTitle}></h1>
              <div className={styles.explanation}>
                <p>{t("intranet.mainHeading")}</p>
              </div>
            </div>
          ) : (
            <div className={styles.chatMessageStream}>
              <div className={styles.chatMessageStreamInner}>
                <div className={styles.explanationSection}>
                  <div className={styles.explanationHeader}>
                    <p className={styles.explanation}>{t("intranet.mainHeading")}</p>
                    <hr className={styles.explanationDivider} />
                  </div>
                </div>

                {answers.map((answerEntry, index) => (
                  <div key={`done-${index}`}>
                    <div className={styles.userMessageRow}>
                      <UserChatMessage message={answerEntry[0]} />
                    </div>
                    <div className={styles.chatMessageGpt}>
                      <Answer
                        isStreaming={false}
                        answer={answerEntry[1]}
                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                        onCitationClicked={c => onShowCitation(c, index)}
                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                        showFollowupQuestions={useSuggestFollowupQuestions}
                      />
                    </div>
                  </div>
                ))}

                {isStreaming &&
                  streamedAnswers.map((streamedAnswer, idx) => (
                    <div key={`stream-${idx}`}>
                      <UserChatMessage message={streamedAnswer[0]} />
                      <div className={styles.chatMessageGpt}>
                        <Answer
                          isStreaming
                          answer={streamedAnswer[1]}
                          isSelected={false}
                          onCitationClicked={c => onShowCitation(c, answers.length + idx)}
                          onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, answers.length + idx)}
                          onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, answers.length + idx)}
                          onFollowupQuestionClicked={q => makeApiRequest(q)}
                          showFollowupQuestions={useSuggestFollowupQuestions}
                        />
                      </div>
                    </div>
                  ))}

                {isLoading && (
                  <>
                    <UserChatMessage message={lastQuestionRef.current} />
                    <div className={styles.chatMessageGptMinWidth}>
                      <AnswerLoading />
                    </div>
                  </>
                )}

                {error ? (
                  <>
                    <UserChatMessage message={lastQuestionRef.current} />
                    <div className={styles.chatMessageGptMinWidth}>
                      <AnswerError error={String((error as any)?.message ?? error)} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                    </div>
                  </>
                ) : null}

                <div ref={chatMessageStreamEnd} />
              </div>
            </div>
          )}
          {/* INPUT AREA */}
          <div className={styles.chatInput}>
            <div className={styles.chatInputInner}>
              {answers.length > 0 && (
                  <ClearChatButton
                    className={styles.commandButton}
                    onClick={clearChat}
                    disabled={!lastQuestionRef.current || isLoading}
                  />
              )}

              <div
                className={styles.chatInputWrapper}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setTimeout(() => setIsInputFocused(false), 150)}
                tabIndex={-1}
              >
                <QuestionInput
                  clearOnSend
                  placeholder={t("search.prompt")}
                  disabled={isLoading}
                  onSend={makeApiRequest}
                  initQuestion={suggestionText}
                />

                {isInputFocused && !isLoading && !isStreaming && answers.length === 0 && (
                  <div className={styles.suggestionDropdown}>
                      <div className={styles.suggestionHeader}>
                        {t("examples.subheading")}
                      </div>
                    <ul className={styles.suggestionList}>
                      {examples.map(ex => (
                        <li key={ex} className={styles.suggestionItem}>
                          <div className={styles.suggestionIcon}>
                            <img src={searchIcon} alt="Search" />
                          </div>
                          <a
                            href="#"
                            className={styles.suggestionLink}
                            onMouseDown={e => {
                              e.preventDefault();
                              setSuggestionText(ex);
                            }}
                          >
                            {ex}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className={styles.chatDisclaimer}>
                  {tWithFallback(
                    isMobile ? "meta.disclaimer.mobile" : "meta.disclaimer",
                    "meta.disclaimer"
                  )}{" "}
                  <a
                    href={disclaimerUrls[lang] ?? disclaimerUrls.en}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {tWithFallback(
                      isMobile ? "meta.disclaimer.link.mobile" : "meta.disclaimer.link",
                      "meta.disclaimer.link"
                    )}
                </a>
              </div>

              <div className={styles.chatDisclaimer}>
                  {tWithFallback(
                    isMobile ? "meta.help.mobile" : "meta.help",
                    "meta.help"
                  )}{" "}
                  <a
                    href={helpUrls[lang] ?? helpUrls.en}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {tWithFallback(
                      isMobile ? "meta.help.link.mobile" : "meta.help.link",
                      "meta.help.link"
                    )}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
